import type { CollateralKey, CollateralParams, PriceFeed, Vault } from "./candid-helpers";
import {
	DISPLAY_BORROW_RATE_APR,
	DISPLAY_MAX_LTV,
	RISK_THRESHOLDS,
	type RiskZone,
} from "./protocol-constants";

/** LTV = debt / collateralValue × 100 */
export function calculateLtv(debt: number, collateralValue: number): number {
	if (collateralValue <= 0) return debt > 0 ? 100 : 0;
	return (debt / collateralValue) * 100;
}

/** Health factor = collateralValue / debt (∞ when no debt) */
export function calculateHealthFactor(
	collateralValue: number,
	debt: number,
): number {
	if (debt <= 0) return Number.POSITIVE_INFINITY;
	return collateralValue / debt;
}

/** Max LTV enforced by protocol from min collateral ratio */
export function protocolMaxLtv(minCollateralRatio: number): number {
	if (minCollateralRatio <= 0) return DISPLAY_MAX_LTV;
	return 100 / minCollateralRatio;
}

/** Effective max LTV for UI — min of display cap and on-chain limit */
export function effectiveMaxLtv(minCollateralRatio: number): number {
	return Math.min(DISPLAY_MAX_LTV, protocolMaxLtv(minCollateralRatio));
}

/** Max additional USDM borrowable given collateral value and existing debt */
export function calculateMaxBorrow(
	collateralValue: number,
	existingDebt: number,
	minCollateralRatio: number,
): number {
	if (collateralValue <= 0) return 0;
	const maxDebt = collateralValue / minCollateralRatio;
	return Math.max(0, maxDebt - existingDebt);
}

/** Sum USD value of vault collateral using price feeds */
export function vaultCollateralValue(
	vault: Vault,
	getPrice: (asset: CollateralKey) => number,
): number {
	let total = 0;
	for (const [type, amount] of vault.collateral) {
		total += amount * getPrice(type);
	}
	return total;
}

/** Projected collateral value after multi-asset deposits (amounts in native units). */
export function projectedCollateralValueMulti(
	vault: Vault | null,
	deposits: Partial<Record<CollateralKey, number>>,
	getPrice: (asset: CollateralKey) => number,
): number {
	let total = 0;
	if (vault) {
		for (const [type, amount] of vault.collateral) {
			total += amount * getPrice(type);
		}
	}
	for (const [asset, amount] of Object.entries(deposits) as [
		CollateralKey,
		number,
	][]) {
		if (amount > 0) {
			total += amount * getPrice(asset);
		}
	}
	return total;
}

/** Projected LTV after minting additional USDM */
export function projectedLtv(
	collateralValue: number,
	existingDebt: number,
	additionalMint: number,
): number {
	return calculateLtv(existingDebt + additionalMint, collateralValue);
}

export function getRiskZone(ltv: number, liquidationLtv?: number): RiskZone {
	const liq = liquidationLtv ?? RISK_THRESHOLDS.moderate;
	if (ltv >= liq + 5) return "liquidatable";
	if (ltv >= RISK_THRESHOLDS.moderate) return "danger";
	if (ltv >= RISK_THRESHOLDS.safe) return "moderate";
	return "safe";
}

export function riskZoneColor(zone: RiskZone): string {
	switch (zone) {
		case "safe":
			return "#35af00";
		case "moderate":
			return "#F59E0B";
		case "danger":
			return "#EF4444";
		case "liquidatable":
			return "#DC2626";
	}
}

export function buildPriceMap(feeds: PriceFeed[]): Map<CollateralKey, number> {
	const map = new Map<CollateralKey, number>();
	for (const feed of feeds) {
		map.set(feed.asset, feed.price);
	}
	return map;
}

export function getBorrowRateApr(
	collateralParams?: CollateralParams[],
	collateralType?: CollateralKey,
): number {
	if (collateralParams && collateralType) {
		const match = collateralParams.find(
			(p) => p.collateralType === collateralType,
		);
		if (match) return Number(match.stabilityFeeBps) / 100;
	}
	if (collateralParams && collateralParams.length > 0) {
		return Number(collateralParams[0]!.stabilityFeeBps) / 100;
	}
	return DISPLAY_BORROW_RATE_APR;
}
